import React from 'react';
import { useNavigate } from 'react-router-dom';
import BottomSheet from '../../shared/components/BottomSheet';
import ListRow from '../../shared/components/ListRow';
import Avatar from '../../shared/components/Avatar';
import { MdAdd, MdCheck } from '../../shared/icons';
import { useAppStore } from '../../shared/stores/appStore';
import { useValidatedBabyId } from '../../shared/hooks/useValidatedBabyId';
import { getBabyAgeText } from '../../shared/utils/milestoneHelper';

/**
 * BabySelector — the baby switcher, opened from the header baby chip in Layout.
 *
 *   <BabySelector isOpen={open} onClose={() => setOpen(false)} />
 *
 * A bottom sheet rather than a row of header pills: on a phone the pill row
 * only ever showed initials, every pill was under 44 px, and the row competed
 * with the timer pills for the same 343 pt of header.
 *
 * This component *sets* the selection, so it is one of the two sanctioned
 * places that touch `setSelectedBabyId` directly (see useValidatedBabyId).
 */
export default function BabySelector({ isOpen, onClose }) {
  const setSelectedBabyId = useAppStore((s) => s.setSelectedBabyId);
  const { babies, selectedBabyId } = useValidatedBabyId();
  const navigate = useNavigate();

  const choose = (babyId) => {
    setSelectedBabyId(babyId);
    if (onClose) onClose();
  };

  const addBaby = () => {
    if (onClose) onClose();
    navigate('/baby-setup');
  };

  return (
    <BottomSheet isOpen={!!isOpen} onClose={onClose} title="Switch baby">
      <ul className="list-none m-0 p-0 divide-y divide-surface-200 dark:divide-night-border">
        {babies.map((baby, i) => {
          const selected = baby.babyId === selectedBabyId;
          return (
            <ListRow
              key={baby.babyId}
              as="li"
              leading={<Avatar name={baby.name} index={i} size="md" />}
              title={baby.name || 'Baby'}
              subtitle={getBabyAgeText(baby.dateOfBirth) || undefined}
              trailing={
                selected ? (
                  <MdCheck className="w-5 h-5 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                ) : null
              }
              onPress={() => choose(baby.babyId)}
              aria-current={selected ? 'true' : undefined}
            />
          );
        })}
        <ListRow
          as="li"
          leading={
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300">
              <MdAdd className="w-5 h-5" aria-hidden="true" />
            </span>
          }
          title="Add baby"
          onPress={addBaby}
        />
      </ul>
    </BottomSheet>
  );
}
